import { useState, useRef } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { COUNTRIES, JOB_TITLES } from '../constants';

interface SubmitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

const PROVIDERS = [
  { id: 'openai', label: 'OpenAI' },
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'github', label: 'GitHub Copilot' },
  { id: 'google', label: 'Google (Gemini)' },
  { id: 'alibaba', label: 'Alibaba (Qwen)' },
  { id: 'deepseek', label: 'DeepSeek' },
  { id: 'mistral', label: 'Mistral' },
  { id: 'perplexity', label: 'Perplexity' },
  { id: 'cohere', label: 'Cohere' },
  { id: 'meta', label: 'Meta (Llama)' },
];

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

type Step = 'form' | 'otp';

export default function SubmitModal({ isOpen, onClose, onSubmitted }: SubmitModalProps) {
  // Form fields
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [country, setCountry] = useState('');
  const [maxSpend, setMaxSpend] = useState('');
  const [actualSpend, setActualSpend] = useState('');
  const [unlimited, setUnlimited] = useState(false);
  const [email, setEmail] = useState('');
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);

  // Verification state
  const [step, setStep] = useState<Step>('form');
  const [emailHash, setEmailHash] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  if (!isOpen) return null;

  const toggleProvider = (id: string) => {
    setSelectedProviders((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const resetAll = () => {
    setCompany(''); setRole(''); setCountry(''); setMaxSpend(''); setActualSpend(''); setUnlimited(false);
    setEmail(''); setSelectedProviders([]); setStep('form');
    setEmailHash(''); setEmailDomain(''); setOtpCode('');
    setTurnstileToken(null); setError(null); setLoading(false);
    turnstileRef.current?.reset();
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  // ── Step 1: Request OTP ───────────────────────────────────────
  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!turnstileToken) {
      setError('Please wait for the security check to complete.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/verify/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          company,
          cfTurnstileResponse: turnstileToken,
        }),
      });

      let data: any = {};
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await res.json();
      }

      if (!res.ok) {
        throw new Error(data.error || 'Verification request failed. (Is the backend running?)');
      }

      setEmailHash(data.emailHash);
      setEmailDomain(data.emailDomain);
      setStep('otp');
    } catch (err: any) {
      setError(err.message);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Submit with OTP ───────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    if (e) e.preventDefault();

    const codeToSubmit = otpCode;
    if (codeToSubmit.length !== 6) {
      setError('Please enter the 6-digit code from your email.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company,
          role,
          country,
          max_spend: unlimited ? null : (maxSpend ? Number(maxSpend.toString().replace(/,/g, '')) : null),
          unlimited: unlimited,
          actual_spend: actualSpend ? Number(actualSpend.toString().replace(/,/g, '')) : null,
          providers: selectedProviders,
          emailHash,
          emailDomain,
          otpCode: codeToSubmit,
        }),
      });

      let data: any = {};
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await res.json();
      }

      if (!res.ok) {
        throw new Error(data.error || 'Submission failed. (Is the backend running?)');
      }

      handleClose();
      onSubmitted();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────
  return (
    <div
      id="submit-modal-overlay"
      style={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        backgroundColor: 'rgba(27, 58, 92, 0.35)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        padding: '1rem',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        id="submit-modal"
        className="glass-panel"
        style={{
          width: '100%', maxWidth: '560px', padding: '2rem',
          backgroundColor: 'var(--bg-secondary)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.75rem' }}>
          <div>
            <h3 style={{ fontSize: '1.4rem', margin: 0 }}>
              {step === 'form' ? 'Submit Token Spend' : 'Verify Your Email'}
            </h3>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem' }}>
              {step === 'form'
                ? 'Verified with your work email. No PII stored.'
                : `Code sent to your ${emailDomain} address`}
            </p>
          </div>
          <button id="modal-close-btn" className="btn btn-secondary" style={{ padding: '0.4rem 0.75rem', fontSize: '1rem' }} onClick={handleClose}>✕</button>
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
          <div style={{
            flex: 1, height: '3px', borderRadius: '2px',
            background: 'var(--accent-primary)',
          }} />
          <div style={{
            flex: 1, height: '3px', borderRadius: '2px',
            background: step === 'otp' ? 'var(--accent-primary)' : 'var(--border-color)',
            transition: 'background 0.3s ease',
          }} />
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: 'rgba(199, 80, 80, 0.08)', border: '1px solid rgba(199, 80, 80, 0.25)', borderRadius: 'var(--radius-sm)', padding: '0.75rem 1rem', marginBottom: '1.25rem', fontSize: '0.875rem', color: '#C75050' }}>
            {error}
          </div>
        )}

        {/* ═══ STEP 1: FORM ═══ */}
        {step === 'form' && (
          <form onSubmit={handleVerifyEmail}>
            {/* Company, Role & Country */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="input-group" style={{ marginBottom: 0 }}>
                <label className="input-label">Company Name</label>
                <input id="input-company" type="text" className="input-field" placeholder="e.g. Acme Corp" required
                  value={company} onChange={(e) => setCompany(e.target.value)} />
              </div>
              <div className="input-group" style={{ marginBottom: 0 }}>
                <label className="input-label">Your Role</label>
                <select id="input-role" className="input-field" required
                  value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="">Select role…</option>
                  {JOB_TITLES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="input-group" style={{ marginTop: '1.25rem', marginBottom: 0 }}>
              <label className="input-label">Country</label>
              <select
                id="input-country"
                className="input-field"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
              >
                <option value="">Select country…</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* Spend fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1.25rem' }}>
              <div className="input-group" style={{ marginBottom: 0 }}>
                <label className="input-label">
                  Max Allowed Spend / Month <span style={{ color: 'var(--accent-primary)' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '0.9rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }}>$</span>
                  <input id="input-max-spend" type="text" inputMode="numeric" pattern="[0-9]*" className="input-field" placeholder="50000" required
                    style={{ paddingLeft: '1.75rem' }}
                    value={maxSpend} onChange={(e) => setMaxSpend(e.target.value.replace(/[^0-9]/g, ''))} disabled={unlimited} />
                  <label style={{ marginLeft: '0.5rem', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input type="checkbox" checked={unlimited} onChange={(e) => { setUnlimited(e.target.checked); if (e.target.checked) setMaxSpend(''); }} style={{ marginRight: '0.25rem' }} />
                    Unlimited
                  </label>
                </div>
              </div>
              <div className="input-group" style={{ marginBottom: 0 }}>
                <label className="input-label">Actual Spend / Month <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(optional)</span></label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '0.9rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', pointerEvents: 'none' }}>$</span>
                  <input id="input-actual-spend" type="text" inputMode="numeric" pattern="[0-9]*" className="input-field" placeholder="12000"
                    style={{ paddingLeft: '1.75rem' }}
                    value={actualSpend} onChange={(e) => setActualSpend(e.target.value.replace(/[^0-9]/g, ''))} />
                </div>
              </div>
            </div>

            {/* Providers */}
            <div style={{ marginTop: '1.5rem' }}>
              <label className="input-label" style={{ marginBottom: '0.75rem', display: 'block' }}>
                Approved Providers <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>(select all that apply)</span>
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {PROVIDERS.map((p) => {
                  const checked = selectedProviders.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      id={`provider-${p.id}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.45rem',
                        padding: '0.45rem 0.9rem',
                        borderRadius: 'var(--radius-full)',
                        border: `1px solid ${checked ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                        backgroundColor: checked ? 'rgba(74,123,167,0.1)' : 'var(--bg-glass)',
                        cursor: 'pointer', fontSize: '0.875rem',
                        fontWeight: checked ? 500 : 400,
                        color: checked ? 'var(--accent-primary)' : 'var(--text-secondary)',
                        transition: 'all var(--transition-fast)', userSelect: 'none',
                      }}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleProvider(p.id)} style={{ display: 'none' }} />
                      {checked && <span style={{ fontSize: '0.75rem' }}>✓</span>}
                      {p.label}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Work Email */}
            <div className="input-group" style={{ marginTop: '1.5rem', marginBottom: 0 }}>
              <label className="input-label">
                Work Email <span style={{ color: 'var(--accent-primary)' }}>*</span>
                <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginLeft: '0.5rem' }}>— we'll send a code, never store your email</span>
              </label>
              <input id="input-email" type="email" className="input-field" placeholder="you@company.com" required
                value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            {/* Turnstile */}
            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token) => setTurnstileToken(token)}
                onError={() => setTurnstileToken(null)}
                onExpire={() => setTurnstileToken(null)}
                options={{ theme: 'light', size: 'flexible' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={handleClose} disabled={loading}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ flex: 1, opacity: !turnstileToken ? 0.5 : 1 }}
                disabled={loading || !turnstileToken}
              >
                {loading ? 'Sending code…' : !turnstileToken ? 'Verifying…' : 'Verify Email →'}
              </button>
            </div>
          </form>
        )}

        {/* ═══ STEP 2: OTP ═══ */}
        {step === 'otp' && (
          <form onSubmit={handleSubmit}>
            <div style={{
              textAlign: 'center', padding: '1.5rem',
              background: 'var(--bg-glass)', borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-color)', marginBottom: '1.5rem',
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📧</div>
              <p style={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '0.25rem' }}>
                Check your inbox
              </p>
              <p style={{ fontSize: '0.875rem' }}>
                We sent a 6-digit code to your <strong style={{ color: 'var(--text-primary)' }}>@{emailDomain}</strong> address
              </p>
            </div>

            <div className="input-group" style={{ marginBottom: 0 }}>
              <label className="input-label">Verification Code</label>
              <input
                id="input-otp"
                type="text"
                className="input-field"
                placeholder="123456"
                maxLength={6}
                required
                autoFocus
                value={otpCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtpCode(val);
                }}
                style={{
                  textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em',
                  fontWeight: 700, fontFamily: 'monospace',
                }}
              />
            </div>

            <p style={{ fontSize: '0.8rem', marginTop: '0.75rem', textAlign: 'center' }}>
              Code expires in 10 minutes.{' '}
              <button
                type="button"
                style={{
                  background: 'none', border: 'none', color: 'var(--accent-primary)',
                  cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'underline',
                  fontFamily: 'inherit',
                }}
                onClick={() => { setStep('form'); setOtpCode(''); setError(null); }}
              >
                Resend code
              </button>
            </p>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={() => { setStep('form'); setOtpCode(''); setError(null); }}
                disabled={loading}
              >
                ← Back
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ flex: 1, opacity: otpCode.length !== 6 ? 0.5 : 1 }}
                disabled={loading || otpCode.length !== 6}
              >
                {loading ? 'Submitting…' : 'Submit Data'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
